import React, { useRef, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  FiClock,
  FiSettings,
  FiMoreHorizontal,
  FiPlus,
  FiUser,
  FiBriefcase,
  FiCalendar,
  FiTarget,
  FiTag,
  FiFileText,
  FiLayers,
  FiCreditCard,
  FiPhone,
  FiMail
} from "react-icons/fi";

export default function KanbanBoard({ columns, setColumns, onCardClick }) {
  const containerRef = useRef(null);
  const isDraggingCard = useRef(false);
  const isGrabbingContainer = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const autoScrollIntervalRef = useRef(null);
  const scrollDirectionRef = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseDown = (e) => {
      if (isDraggingCard.current) return;
      if (e.target.closest('[data-draggable="true"]')) return;

      isGrabbingContainer.current = true;
      startX.current = e.pageX - container.offsetLeft;
      scrollLeft.current = container.scrollLeft;
      container.style.cursor = 'grabbing';
    };

    const handleMouseLeave = () => {
      isGrabbingContainer.current = false;
      container.style.cursor = 'grab';
    };

    const handleMouseUp = () => {
      isGrabbingContainer.current = false;
      container.style.cursor = 'grab';
    };

    const handleMouseMove = (e) => {
      if (!isGrabbingContainer.current) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX.current) * 1.5;
      container.scrollLeft = scrollLeft.current - walk;
    };

    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mouseleave', handleMouseLeave);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mousemove', handleMouseMove);

    container.style.cursor = 'grab';

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mousemove', handleMouseMove);
    };
  }, [containerRef]);

  const startAutoScroll = () => {
    stopAutoScroll();
    const container = containerRef.current;
    if (!container) return;
    autoScrollIntervalRef.current = setInterval(() => {
      container.scrollLeft += scrollDirectionRef.current * 8;
    }, 16);
  };

  const stopAutoScroll = () => {
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  };

  const updateAutoScroll = (clientX) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const SCROLL_ZONE = 150;
    if (clientX < rect.left + SCROLL_ZONE) {
      scrollDirectionRef.current = -1;
      startAutoScroll();
    } else if (clientX > rect.right - SCROLL_ZONE) {
      scrollDirectionRef.current = 1;
      startAutoScroll();
    } else {
      scrollDirectionRef.current = 0;
      stopAutoScroll();
    }
  };

  const handleDragStart = () => {
    isDraggingCard.current = true;
    document.addEventListener("mousemove", handleMouseMoveForAutoScroll);
  };

  const handleMouseMoveForAutoScroll = (e) => {
    if (isDraggingCard.current) updateAutoScroll(e.clientX);
  };

  const handleDragEnd = (result) => {
    isDraggingCard.current = false;
    stopAutoScroll();
    document.removeEventListener("mousemove", handleMouseMoveForAutoScroll);

    if (!result.destination) return;
    if (
      result.destination.droppableId === result.source.droppableId &&
      result.destination.index === result.source.index
    ) {
      return;
    }

    const sourceCol = columns[result.source.droppableId];
    const destCol = columns[result.destination.droppableId];
    const sourceItems = [...sourceCol.items];
    const destItems = [...destCol.items];

    const [movedItem] = sourceItems.splice(result.source.index, 1);
    destItems.splice(result.destination.index, 0, { ...movedItem, columnId: result.destination.droppableId });

    setColumns({
      ...columns,
      [result.source.droppableId]: {
        ...sourceCol,
        items: sourceItems,
        count: sourceItems.length
      },
      [result.destination.droppableId]: {
        ...destCol,
        items: destItems,
        count: destItems.length
      }
    });
  };

  const TagBadge = ({ text }) => (
    <span className={`text-xs px-2 py-0.5 rounded-full ${
      text.toLowerCase().includes('departamento') 
        ? 'bg-pink-100 text-pink-700' 
        : 'bg-purple-100 text-purple-700'
    }`}>
      {text}
    </span>
  );

  const TimeIcon = ({ days, type }) => {
    let color;
    switch (type) {
      case "warning":
        color = "#f97316"; 
        break;
      case "info":
        color = "#3b82f6";
        break;
      case "primary":
      default:
        color = "#9333ea";
        break;
    }

    return (
      <div className="flex items-center gap-1 text-xs text-gray-700">
        <FiClock size={14} color={color} />
        <span>{days}d</span>
      </div>
    );
  };

  if (!columns || Object.keys(columns).length === 0) {
    return <div className="text-center text-gray-500 p-4">Nenhuma coluna para exibir.</div>;
  }

  const formatarVencimento = (dataISO) => {
    if (!dataISO) return null;
    const data = new Date(dataISO);
    const mes = data.toLocaleString("pt-BR", { month: "short" });
    const dia = data.getDate();
    return `Venc ${mes}, ${dia}`;
  };  
  
  return (
    <div
      className="kanban-container w-full overflow-hidden h-[78vh] min-h-[500px] max-h-[90vh]"
    >
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div
          ref={containerRef}
          className="flex gap-4 pb-6 h-full overflow-auto px-4"
        >
          {Object.values(columns).map((column) => (
            <div key={column.id} className="flex flex-col h-full flex-shrink-0 w-[300px]">
              <div className="flex items-center justify-between mb-2 px-2 py-1 rounded-t-md border-t-2 bg-white">
                <div className="flex items-center gap-2">
                  <h2 className="text-gray-700 text-sm font-medium">{column.title}</h2>
                  <span className="text-xs bg-gray-100 px-2 rounded-full">{column.count}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button><FiSettings size={16} /></button>
                  <button><FiMoreHorizontal size={16} /></button>
                  <button><FiPlus size={16} /></button>
                </div>
              </div>

              <Droppable droppableId={column.id}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="rounded-md p-2 flex-1 min-h-[200px] w-[300px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 bg-gray-50 border border-gray-200"
                  >
                    {(column.items ?? []).map((item, index) => (
                      <Draggable key={item.id} draggableId={item.id} index={index}>
                        {(provided) => (
                          <div
                            data-draggable="true"
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="bg-white rounded-lg border shadow p-4 mb-4 hover:shadow-md transition cursor-pointer"
                            onClick={() => onCardClick(item, column.id)}
                          >
                            <div className="flex flex-wrap gap-2 mb-2">
                              {item.tags?.map((tag, idx) => (
                                <TagBadge key={idx} text={tag} />
                              ))}
                            </div>

                            <h3 className="text-sm font-semibold text-gray-800 mb-3 uppercase">
                              {item.titulo || item.service || "SEM TÍTULO"}
                            </h3>

                            <div className="flex flex-col space-y-2 text-xs text-gray-700">
                              {item.client && (
                                <div className="flex items-center gap-2">
                                  <FiBriefcase size={14} className="text-gray-500" />
                                  <span className="font-normal text-gray-500">Cliente</span>
                                  <span className="truncate">{item.client}</span>
                                </div>
                              )}
                              {item.cnpj && (
                                <div className="flex items-center gap-2">
                                  <FiCreditCard size={14} className="text-gray-500" />
                                  <span className="font-normal text-gray-500">CNPJ</span>
                                  <span className="truncate">{item.cnpj}</span>
                                </div>
                              )}
                              {item.telefone && (
                                <div className="flex items-center gap-2">
                                  <FiPhone size={14} className="text-gray-500" />
                                  <span className="font-normal text-gray-500">Telefone</span>
                                  <span className="truncate">{item.telefone}</span>
                                </div>
                              )}
                              {item.email && (
                                <div className="flex items-center gap-2">
                                  <FiMail size={14} className="text-gray-500" />
                                  <span className="font-normal text-gray-500">Email</span>
                                  <span className="truncate">{item.email}</span>
                                </div>
                              )}
                              {item.acumulador && (
                                <div className="flex items-center gap-2">
                                  <FiTag size={14} className="text-gray-500" />
                                  <span className="font-normal text-gray-500">Acumulador</span>
                                  <span className="truncate">{item.acumulador}</span>
                                </div>
                              )}
                              {item.competencia && (
                                <div className="flex items-center gap-2">
                                  <FiCalendar size={14} className="text-gray-500" />
                                  <span className="font-normal text-gray-500">Competência</span>
                                  <span className="truncate">{item.competencia}</span>
                                </div>
                              )}
                              {item.prazoMeta && (
                                <div className="flex items-center gap-2">
                                  <FiTarget size={14} className="text-gray-500" />
                                  <span className="font-normal text-gray-500">Prazo Meta</span>
                                  <span className="truncate">{item.prazoMeta}</span>
                                </div>
                              )}
                              {item.responsavel && (
                                <div className="flex items-center gap-2">
                                  <FiUser size={14} className="text-gray-500" />
                                  <span className="font-normal text-gray-500">Responsável</span>
                                  <span className="truncate">{item.responsavel}</span>
                                </div>
                              )}
                              {item.departamento && (
                                <div className="flex items-center gap-2">
                                  <FiLayers size={14} className="text-gray-500" />
                                  <span className="font-normal text-gray-500">Departamento</span>
                                  <span className="truncate">{item.departamento}</span>
                                </div>
                              )}
                              {item.regime && (
                                <div className="flex items-center gap-2">
                                  <FiFileText size={14} className="text-gray-500" />
                                  <span className="font-normal text-gray-500">Regime</span>
                                  <span className="truncate">{item.regime}</span>
                                </div>
                              )}
                            </div>


                            {item.vencimento && (
                              <div className="mt-3">
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                                  {formatarVencimento(item.vencimento)}
                                </span>
                              </div>
                            )}

                            {item.reminders && item.reminders.length > 0 && (
                              <div className="flex flex-wrap items-center gap-2 mt-2">
                                {item.reminders.map((r) => (
                                  <TimeIcon key={r.id} type={r.type} days={r.days} />
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
          <div className="flex-shrink-0 w-[300px] mt-9 bg-white/70 rounded-md border border-dashed border-gray-300 h-12 flex items-center justify-center">
            <button className="text-gray-500 hover:text-gray-700 font-medium flex items-center gap-2">
              <FiPlus size={18} />
              Nova fase
            </button>
          </div>
        </div>
      </DragDropContext>
    </div>
  );
}
